import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { PaginationControls } from "metabase/common/components/PaginationControls";
import { usePagination } from "metabase/common/hooks/use-pagination";
import { CacheConfigApi } from "metabase/services";
import { Box, Flex, Loader, Select, Stack, Text, TextInput } from "metabase/ui";
import type { CacheRefreshRun, PaginatedList } from "metabase-types/api";

import Styles from "./StrategyEditorForQuestionsAndDashboards.module.css";
import { formatDateTime } from "./utils";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE = 300;
const RUNNING_POLL_INTERVAL = 5000;

const statusLabel = (status: CacheRefreshRun["status"]) => {
  switch (status) {
    case "running":
      return t`Running`;
    case "success":
      return t`Success`;
    case "failed":
      return t`Failed`;
  }
};

const modelLabel = (model: CacheRefreshRun["model"]) =>
  model === "dashboard" ? t`Dashboard` : t`Question`;

type CacheRefreshTrigger = NonNullable<CacheRefreshRun["details"]>["trigger"];

const triggerLabel = (trigger?: CacheRefreshTrigger) => {
  switch (trigger) {
    case "manual":
      return t`Manual`;
    case "automatic":
      return t`Automatic`;
    default:
      return "-";
  }
};

const queryProgress = (run: CacheRefreshRun) => {
  if (run.details) {
    return `${run.details.completed_query_count}/${run.details.planned_query_count}`;
  }
  return run.query_count ?? "-";
};

const cardProgress = (run: CacheRefreshRun) => {
  if (!run.details) {
    return "-";
  }
  if (run.model === "dashboard") {
    return `${run.details.refreshed_card_count}/${run.details.dashboard_card_count}`;
  }
  return run.details.refreshed_card_count;
};

const tabCount = (run: CacheRefreshRun) => {
  if (run.model !== "dashboard") {
    return "-";
  }
  if (!run.details) {
    return "-";
  }
  return `${run.details.refreshed_tab_count}/${run.details.dashboard_tab_count}`;
};

const succeededQueries = (run: CacheRefreshRun) =>
  run.details?.successful_query_count ?? "-";

const failedQueries = (run: CacheRefreshRun) =>
  run.details?.failed_query_count ?? "-";

export const CacheRefreshHistory = () => {
  const { page, setPage, handleNextPage, handlePreviousPage } = usePagination();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [model, setModel] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [result, setResult] = useState<PaginatedList<CacheRefreshRun> | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [pollTick, setPollTick] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [searchInput, setPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    CacheConfigApi.refreshRuns({
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * page,
      query: search || undefined,
      model: model || undefined,
      status: status || undefined,
    })
      .then((data: PaginatedList<CacheRefreshRun>) => {
        if (!cancelled) {
          setResult(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({ data: [], total: 0 });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, search, model, status, pollTick]);

  const rows = useMemo(() => result?.data ?? [], [result?.data]);
  const total = result?.total ?? 0;

  useEffect(() => {
    if (rows.some((run) => run.status === "running")) {
      const timer = setTimeout(() => {
        setPollTick((tick) => tick + 1);
      }, RUNNING_POLL_INTERVAL);
      return () => clearTimeout(timer);
    }
  }, [rows]);

  const modelOptions = useMemo(
    () => [
      { value: "", label: t`All types` },
      { value: "dashboard", label: t`Dashboards` },
      { value: "question", label: t`Questions` },
    ],
    [],
  );

  const statusOptions = useMemo(
    () => [
      { value: "", label: t`All statuses` },
      { value: "running", label: t`Running` },
      { value: "success", label: t`Success` },
      { value: "failed", label: t`Failed` },
    ],
    [],
  );

  return (
    <Stack gap="md">
      <Flex gap="md" wrap="wrap" align="end">
        <TextInput
          label={t`Search by name`}
          placeholder={t`Dashboard or question name`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          w="20rem"
        />
        <Select
          label={t`Type`}
          data={modelOptions}
          value={model ?? ""}
          onChange={(value) => {
            setPage(0);
            setModel(value || null);
          }}
          w="10rem"
        />
        <Select
          label={t`Status`}
          data={statusOptions}
          value={status ?? ""}
          onChange={(value) => {
            setPage(0);
            setStatus(value || null);
          }}
          w="10rem"
        />
      </Flex>

      {loading ? (
        <Flex justify="center" py="lg">
          <Loader size="sm" />
        </Flex>
      ) : (
        <Box className={Styles.TableScrollContainer}>
          <table className={Styles.HistoryTable}>
            <thead>
              <tr>
                <th>{t`Name`}</th>
                <th>{t`Type`}</th>
                <th>{t`Trigger`}</th>
                <th>{t`Status`}</th>
                <th>{t`Started`}</th>
                <th>{t`Finished`}</th>
                <th>{t`Duration`}</th>
                <th>{t`Queries`}</th>
                <th>{t`Cards`}</th>
                <th>{t`Tabs`}</th>
                <th>{t`Succeeded`}</th>
                <th>{t`Failed`}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((run) => (
                <tr key={run.id}>
                  <td>{run.item_name ?? `#${run.model_id}`}</td>
                  <td className={Styles.CompactCell}>
                    {modelLabel(run.model)}
                  </td>
                  <td className={Styles.CompactCell}>
                    {triggerLabel(run.details?.trigger)}
                  </td>
                  <td
                    className={Styles.CompactCell}
                    title={run.error ?? undefined}
                  >
                    <span
                      className={
                        run.status === "failed"
                          ? Styles.FailedStatus
                          : undefined
                      }
                    >
                      {statusLabel(run.status)}
                    </span>
                    {run.error && (
                      <Text c="text-light" size="xs" mt="xs">
                        {run.error}
                      </Text>
                    )}
                  </td>
                  <td className={Styles.CompactCell}>
                    {formatDateTime(run.started_at)}
                  </td>
                  <td className={Styles.CompactCell}>
                    {formatDateTime(run.finished_at)}
                  </td>
                  <td className={Styles.CompactCell}>
                    {run.duration_ms != null ? `${run.duration_ms}ms` : "-"}
                  </td>
                  <td className={Styles.CompactCell}>{queryProgress(run)}</td>
                  <td className={Styles.CompactCell}>{cardProgress(run)}</td>
                  <td className={Styles.CompactCell}>{tabCount(run)}</td>
                  <td className={Styles.CompactCell}>
                    {succeededQueries(run)}
                  </td>
                  <td className={Styles.CompactCell}>{failedQueries(run)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={12}>
                    <Text c="text-light" fw="bold" ta="center" py="md">
                      {t`No cache refresh runs yet.`}
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      )}

      <PaginationControls
        page={page}
        pageSize={PAGE_SIZE}
        itemsLength={rows.length}
        total={total}
        showTotal
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
      />
    </Stack>
  );
};
